export const PRECEDING_ELEMENT_KEY = '__precedingElement__';
export const arrayToMapWithIndex = (elements) => elements.reduce((acc, element, idx) => {
    acc.set(element.id, [element, idx]);
    return acc;
}, new Map());
const shouldDiscardRemoteElement = (localAppState, local, remote) => {
    var _a, _b, _c;
    if (local &&
        // local element is being edited
        (local.id === ((_a = localAppState.editingElement) === null || _a === void 0 ? void 0 : _a.id) ||
            local.id === ((_b = localAppState.resizingElement) === null || _b === void 0 ? void 0 : _b.id) ||
            local.id === ((_c = localAppState.draggingElement) === null || _c === void 0 ? void 0 : _c.id) ||
            // local element is newer
            local.version > remote.version ||
            // resolve conflicting edits deterministically by taking the one with
            // the lowest versionNonce
            (local.version === remote.version &&
                local.versionNonce < remote.versionNonce))) {
        return true;
    }
    return false;
};
export const reconcileElements = (localElements, remoteElements, localAppState) => {
    var _a;
    const localElementsData = arrayToMapWithIndex(localElements);
    const reconciledElements = localElements.slice();
    const duplicates = new WeakMap();
    let cursor = 0;
    let offset = 0;
    let remoteElementIdx = -1;
    for (const remoteElement of remoteElements) {
        remoteElementIdx++;
        const local = localElementsData.get(remoteElement.id);
        if (shouldDiscardRemoteElement(localAppState, local === null || local === void 0 ? void 0 : local[0], remoteElement)) {
            if (remoteElement[PRECEDING_ELEMENT_KEY]) {
                delete remoteElement[PRECEDING_ELEMENT_KEY];
            }
            continue;
        }
        // Mark duplicate for removal as it'll be replaced with the remote element
        if (local) {
            // Unless the remote and local elements are the same element in which case
            // we need to keep it as we'd otherwise discard it from the resulting
            // array.
            if (local[0] === remoteElement) {
                continue;
            }
            duplicates.set(local[0], true);
        }
        // parent may not be defined in case the remote client is running an older
        // excalidraw version
        const parent = remoteElement[PRECEDING_ELEMENT_KEY] ||
            ((_a = remoteElements[remoteElementIdx - 1]) === null || _a === void 0 ? void 0 : _a.id) ||
            null;
        if (parent != null) {
            delete remoteElement[PRECEDING_ELEMENT_KEY];
            // ^ indicates the element is the first in elements array
            if (parent === '^') {
                offset++;
                if (cursor === 0) {
                    reconciledElements.unshift(remoteElement);
                    localElementsData.set(remoteElement.id, [
                        remoteElement,
                        cursor - offset,
                    ]);
                }
                else {
                    reconciledElements.splice(cursor + 1, 0, remoteElement);
                    localElementsData.set(remoteElement.id, [
                        remoteElement,
                        cursor + 1 - offset,
                    ]);
                    cursor++;
                }
            }
            else {
                let idx = localElementsData.has(parent)
                    ? localElementsData.get(parent)[1]
                    : null;
                if (idx != null) {
                    idx += offset;
                }
                if (idx != null && idx >= cursor) {
                    reconciledElements.splice(idx + 1, 0, remoteElement);
                    offset++;
                    localElementsData.set(remoteElement.id, [
                        remoteElement,
                        idx + 1 - offset,
                    ]);
                    cursor = idx + 1;
                }
                else if (idx != null) {
                    reconciledElements.splice(cursor + 1, 0, remoteElement);
                    offset++;
                    localElementsData.set(remoteElement.id, [
                        remoteElement,
                        cursor + 1 - offset,
                    ]);
                    cursor++;
                }
                else {
                    reconciledElements.push(remoteElement);
                    localElementsData.set(remoteElement.id, [
                        remoteElement,
                        reconciledElements.length - 1 - offset,
                    ]);
                }
            }
            // no parent z-index information, local element exists → replace in place
        }
        else if (local) {
            reconciledElements[local[1]] = remoteElement;
            localElementsData.set(remoteElement.id, [remoteElement, local[1]]);
            // otherwise push to the end
        }
        else {
            reconciledElements.push(remoteElement);
            localElementsData.set(remoteElement.id, [
                remoteElement,
                reconciledElements.length - 1 - offset,
            ]);
        }
    }
    const ret = reconciledElements.filter((element) => !duplicates.has(element));
    return ret;
};
